import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { colors } from "../../../Shared/colors";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useGetRevenueAnalyticsQuery } from "../../../features/accountSlice";
import { 
  RevenueGraphContainer, 
  CustomTooltip, 
  ChartSkeleton
} from "../../../Shared/ChartComponents";

const monthNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

export const RevenueGraph = ({ staff }) => {
  const { data: revenueData, isFetching } = useGetRevenueAnalyticsQuery({ 
    employeeId: staff?.id,
    year: new Date().getFullYear()
  });

  const [chartData, setChartData] = useState([]);
  const [revenueSummary, setRevenueSummary] = useState({
    totalDP: 0,
    totalPDP: 0,
    totalTP: 0
  });

  useEffect(() => {
    // Use ONLY real API data - no dummy data fallback
    if (revenueData?.monthlyData) {
      // Process real API data
      const monthData = revenueData.monthlyData.map((report, index) => ({
        name: monthNames[index],
        DP: report.dpAmount || 0,
        PDP: report.pdpAmount || 0,
        TP: report.tpAmount || 0,
        month: report.month
      }));

      setChartData(monthData);
      setRevenueSummary(revenueData.summary || { totalDP: 0, totalPDP: 0, totalTP: 0 });
    } else {
      // Initialize with empty data until API responds
      const emptyData = Array.from({ length: 12 }, (_, index) => ({
        name: monthNames[index],
        DP: 0,
        PDP: 0,
        TP: 0,
        month: index + 1
      }));
      
      setChartData(emptyData);
      setRevenueSummary({ totalDP: 0, totalPDP: 0, totalTP: 0 });
    }
  }, [revenueData]);

  if (isFetching) {
    return <ChartSkeleton />;
  }

  return (
    <RevenueGraphContainer>
      <div className="chart-header">
        <div>
          <h3>Company Revenue Analytics</h3>
          <p>
            Monthly payment breakdown and totals
            {revenueData?.year && (
              <span style={{ marginLeft: '10px', fontSize: '0.9em', color: '#666' }}>
                ({revenueData.year})
                {revenueData.requestedYear && revenueData.year !== revenueData.requestedYear && (
                  <span style={{ color: '#ff6b6b' }}>
                    {' '}• Showing {revenueData.year} data (no data for {revenueData.requestedYear})
                  </span>
                )}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Revenue Summary Cards */}
      <RevenueSummaryGrid>
        <SummaryCard>
          <SummaryIcon style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
            <span>DP</span>
          </SummaryIcon>
          <SummaryContent>
            <SummaryLabel>Down Payment</SummaryLabel>
            <SummaryValue>PKR {revenueSummary.totalDP.toLocaleString()}</SummaryValue>
          </SummaryContent>
        </SummaryCard>

        <SummaryCard>
          <SummaryIcon style={{ background: 'linear-gradient(135deg, #f093fb, #f5576c)' }}>
            <span>PDP</span>
          </SummaryIcon>
          <SummaryContent>
            <SummaryLabel>Partial Down Payment</SummaryLabel>
            <SummaryValue>PKR {revenueSummary.totalPDP.toLocaleString()}</SummaryValue>
          </SummaryContent>
        </SummaryCard>

        <SummaryCard>
          <SummaryIcon style={{ background: 'linear-gradient(135deg, #4facfe, #00f2fe)' }}>
            <span>TP</span>
          </SummaryIcon>
          <SummaryContent>
            <SummaryLabel>Token Payment</SummaryLabel>
            <SummaryValue>PKR {revenueSummary.totalTP.toLocaleString()}</SummaryValue>
          </SummaryContent>
        </SummaryCard>
      </RevenueSummaryGrid>

      {/* Revenue Chart */}
      <ChartWrapper>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <defs>
              <linearGradient id="dpGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#667eea" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#764ba2" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="pdpGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f093fb" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#f5576c" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="tpGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4facfe" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#00f2fe" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
            <XAxis 
              dataKey="name" 
              stroke={colors.text}
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => value}
            />
            <YAxis 
              stroke={colors.text}
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `PKR ${(value / 1000).toFixed(0)}K`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area
              type="monotone"
              dataKey="DP"
              stackId="1"
              stroke="#667eea"
              fill="url(#dpGradient)"
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="PDP"
              stackId="1"
              stroke="#f093fb"
              fill="url(#pdpGradient)"
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="TP"
              stackId="1"
              stroke="#4facfe"
              fill="url(#tpGradient)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartWrapper>
    </RevenueGraphContainer>
  );
};

// Styled Components
const RevenueSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const SummaryCard = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }
`;

const SummaryIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const SummaryContent = styled.div`
  flex: 1;
`;

const SummaryLabel = styled.div`
  font-size: 0.875rem;
  color: ${colors.textSecondary};
  margin-bottom: 0.25rem;
  font-weight: 500;
`;

const SummaryValue = styled.div`
  font-size: 1.25rem;
  font-weight: bold;
  color: ${colors.text};
`;

const ChartWrapper = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
`;